import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  QuizRounded,
  DeleteOutlineRounded,
  AutoAwesomeRounded,
  SearchRounded,
  ExpandMoreRounded,
  ExpandLessRounded,
  ArrowBackRounded,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useTeacherAuth } from '../context/TeacherAuthContext';
import {
  getAssessmentTopics,
  generateAssessment,
  getTeacherAssessments,
  deleteTeacherAssessment,
} from '../services/api';
import { TopicCatalog, TeacherAssessmentList } from '../types';
import { resolveTeacherClassContext } from '../utils/teacherClassContext';
import TeacherLayout from './teacher/TeacherLayout';
import AssessmentEditor from './teacher/AssessmentEditor';
import AssessmentDownloadMenu from './teacher/AssessmentDownloadMenu';

const MAX_TOPICS = 8;

const TeacherAssessments: React.FC = () => {
  const { teacher, token } = useTeacherAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const grades = useMemo(() => {
    const set = new Set<number>();
    (teacher?.classes || []).forEach((c: any) => {
      if (typeof c.grade === 'number') set.add(c.grade);
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [teacher]);

  const initialGrade = useMemo(() => {
    const ctx = resolveTeacherClassContext(location.state as any);
    const g = typeof ctx.grade === 'string' ? parseInt(ctx.grade, 10) : ctx.grade;
    if (typeof g === 'number' && !Number.isNaN(g) && grades.includes(g)) return g;
    return grades[0];
  }, [location.state, grades]);

  const [grade, setGrade] = useState<number | undefined>(initialGrade);
  const [catalog, setCatalog] = useState<TopicCatalog | null>(null);
  const [list, setList] = useState<TeacherAssessmentList | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [openModules, setOpenModules] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => setGrade(initialGrade), [initialGrade]);

  const loadAll = async (g: number) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [cat, lst] = await Promise.all([
        getAssessmentTopics(token, g),
        getTeacherAssessments(token, g),
      ]);
      setCatalog(cat);
      setList(lst);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (grade != null && token) {
      setSelected(new Set());
      loadAll(grade);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade, token]);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else if (next.size < MAX_TOPICS) next.add(key);
      return next;
    });
  };

  const filteredModules = useMemo(() => {
    if (!catalog) return [];
    const q = search.trim().toLowerCase();
    return catalog.modules
      .map((m) => ({
        ...m,
        topics: q
          ? m.topics.filter((t) => t.topic_title.toLowerCase().includes(q))
          : m.topics,
      }))
      .filter((m) => m.topics.length > 0);
  }, [catalog, search]);

  const generate = async () => {
    if (!token || grade == null || selected.size === 0) return;
    setGenerating(true);
    setError(null);
    try {
      const created = await generateAssessment(token, grade, Array.from(selected));
      setSelected(new Set());
      await loadAll(grade);
      setEditingId(created.id); // open the editor on the fresh assessment
    } catch (e: any) {
      setError(e.message || 'Failed to generate assessment');
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (id: number) => {
    if (!token || !window.confirm('Delete this assessment?')) return;
    try {
      await deleteTeacherAssessment(token, id);
      if (grade != null) loadAll(grade);
    } catch (e: any) {
      setError(e.message || 'Failed to delete');
    }
  };

  return (
    <TeacherLayout active="plan">
      <div className="mx-auto w-full max-w-4xl px-4 py-6">
        <button
          onClick={() => navigate('/class-weekly-plan', { state: location.state })}
          className="mb-3 flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-700"
        >
          <ArrowBackRounded fontSize="small" /> Back to Curriculum
        </button>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <QuizRounded className="text-violet-600" /> STEM Assessments
            </h1>
            <p className="text-sm text-slate-500">
              Pick one or more STEM topics and generate an assessment. Edit it, and students get it
              once they finish those topics.
            </p>
          </div>
          {grades.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Grade
              <select
                className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={grade ?? ''}
                onChange={(e) => setGrade(parseInt(e.target.value, 10))}
              >
                {grades.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {error && <div className="mb-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}

        {loading ? (
          <p className="py-10 text-center text-slate-500">Loading…</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {/* --- Left: topic picker --- */}
            <div className="rounded-2xl border border-[#E9E6F4] bg-white p-4">
              <p className="mb-1 text-sm font-bold text-slate-800">1. Choose topics</p>
              <p className="mb-3 text-xs text-slate-400">Select up to {MAX_TOPICS} topics for one assessment.</p>
              <div className="relative mb-3">
                <SearchRounded className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" fontSize="small" />
                <input
                  className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-2 text-sm"
                  placeholder="Search topics…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="max-h-[46vh] overflow-y-auto pr-1">
                {filteredModules.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-400">No topics.</p>
                ) : (
                  filteredModules.map((m) => {
                    const isOpen = openModules.has(m.module_label) || search.trim().length > 0;
                    const selCount = m.topics.filter((t) => selected.has(t.topic_key)).length;
                    return (
                      <div key={m.module_label} className="mb-1.5 rounded-lg border border-slate-100">
                        <button
                          type="button"
                          onClick={() =>
                            setOpenModules((prev) => {
                              const next = new Set(prev);
                              next.has(m.module_label) ? next.delete(m.module_label) : next.add(m.module_label);
                              return next;
                            })
                          }
                          className="flex w-full items-center justify-between px-3 py-2 text-left"
                        >
                          <span className="text-sm font-semibold text-slate-700">
                            {m.module_label}
                            {selCount > 0 && (
                              <span className="ml-2 rounded-full bg-violet-100 px-1.5 text-xs font-semibold text-violet-700">
                                {selCount}
                              </span>
                            )}
                          </span>
                          {isOpen ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
                        </button>
                        {isOpen && (
                          <div className="flex flex-col gap-0.5 px-2 pb-2">
                            {m.topics.map((t) => {
                              const checked = selected.has(t.topic_key);
                              const disabled = !checked && selected.size >= MAX_TOPICS;
                              return (
                                <label
                                  key={t.topic_key}
                                  className={`flex items-start gap-2 rounded-md px-2 py-1.5 text-sm ${
                                    disabled ? 'opacity-40' : 'hover:bg-slate-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => toggle(t.topic_key)}
                                  />
                                  <span className="text-slate-700">{t.topic_title}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                <span className="text-xs font-medium text-slate-500">{selected.size} selected</span>
                <button
                  onClick={generate}
                  disabled={selected.size === 0 || generating}
                  className="flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
                >
                  {generating ? (
                    <CircularProgress size={16} sx={{ color: 'white' }} />
                  ) : (
                    <AutoAwesomeRounded fontSize="small" />
                  )}
                  {generating ? 'Generating…' : 'Generate assessment'}
                </button>
              </div>
              {generating && (
                <p className="mt-2 text-center text-xs text-slate-400">
                  Building questions from the topic content — this can take a few seconds…
                </p>
              )}
            </div>

            {/* --- Right: existing assessments --- */}
            <div className="rounded-2xl border border-[#E9E6F4] bg-white p-4">
              <p className="text-sm font-bold text-slate-800">2. Your assessments</p>
              <p className="mb-3 text-xs text-slate-400">
                Tap one to view &amp; edit its questions, or download it as a PDF.
              </p>
              {!list || list.assessments.length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-400">
                  None yet — generate one from the topics on the left.
                </p>
              ) : (
                <div className="flex max-h-[52vh] flex-col gap-1 overflow-y-auto">
                  {list.assessments.map((a) => (
                    <div
                      key={a.id}
                      className="group flex items-center gap-1 rounded-xl border border-transparent px-2.5 py-2.5 transition-colors hover:border-violet-200 hover:bg-violet-50/50"
                    >
                      <button
                        onClick={() => setEditingId(a.id)}
                        className="min-w-0 flex-1 text-left"
                        title="View & edit"
                      >
                        <p className="truncate text-sm font-semibold text-slate-800 group-hover:text-violet-700">
                          {a.title}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-slate-400">
                          {a.topics.length} topic{a.topics.length !== 1 ? 's' : ''} · {a.question_count} questions
                          {a.edited ? ' · edited' : ''}
                        </p>
                      </button>
                      <div className="flex flex-none items-center gap-0.5">
                        {token && (
                          <AssessmentDownloadMenu
                            token={token}
                            assessmentId={a.id}
                            onError={setError}
                          />
                        )}
                        <button
                          onClick={() => remove(a.id)}
                          className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                          title="Delete"
                          aria-label="Delete assessment"
                        >
                          <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {editingId != null && token && (
        <AssessmentEditor
          token={token}
          assessmentId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => grade != null && loadAll(grade)}
        />
      )}
    </TeacherLayout>
  );
};

export default TeacherAssessments;
